import { api } from '@/lib/api';
import type {
  Location,
  LocationsFilters,
  LocationsResponse,
  V4LocationsResponse,
  CreateLocationInput,
  UpdateLocationInput,
  LocationOptions,
  Account,
  LocationAccountsResponse,
  LocationFormOptions,
  LocationChangelogResponse,
  LocationImageUploadResponse,
  LocationImageDeleteResponse,
} from './types';

// =============================================================================
// Locations API Functions
// Data source: PostgreSQL via GET /v4/locations
// =============================================================================

/**
 * Get locations with optional filtering, sorting, and pagination
 */
export async function getLocations(filters: LocationsFilters = {}): Promise<LocationsResponse> {
  const queryParams: Record<string, string | number | string[]> = {
    pageNumber: filters.pageNumber ?? 0,
    pageLength: filters.pageLength ?? 25,
    sortKey: filters.sortKey ?? 'name',
    sortDirection: filters.sortDirection ?? 1,
    includeEquipmentNames: 'true',
  };

  // Add search filter
  if (filters.search) {
    queryParams.search = filters.search;
  }

  // Add accountId filter
  if (filters.accountId) {
    queryParams.accountId = filters.accountId;
  }

  // Add siteId filter
  if (filters.siteId) {
    queryParams.siteId = filters.siteId;
  }

  // Add tags filter
  if (filters.tags && filters.tags.length > 0) {
    queryParams.tags = JSON.stringify(filters.tags);
  }

  // Add disabled filter
  if (filters.disabled !== undefined) {
    queryParams.disabled = String(filters.disabled);
  }

  const response = await api.get<V4LocationsResponse>('/v4/locations', queryParams);

  return {
    locations: response.locations || [],
    pageNumber: response.pageNumber ?? 0,
    pageLength: response.pageLength ?? 25,
    total: response.total ?? 0,
  };
}

/**
 * Get a single location by ID
 */
export async function getLocation(locationId: string): Promise<Location> {
  const response = await api.get<{ location: Location }>(`/v4/locations/${locationId}`);
  return response.location;
}

/**
 * Get location options (sites dropdown)
 */
export async function getLocationOptions(accountId?: string): Promise<LocationOptions> {
  const params: Record<string, string> = {};
  if (accountId) {
    params.accountId = accountId;
  }
  const response = await api.get<LocationOptions>('/v4/locations/options', params);
  return response;
}

/**
 * Create a new location
 */
export async function createLocation(data: CreateLocationInput): Promise<Location> {
  const response = await api.post<{ location: Location }>('/v4/locations', data);
  return response.location;
}

/**
 * Update a location
 */
export async function updateLocation(locationId: string, data: UpdateLocationInput): Promise<Location> {
  const response = await api.put<{ location: Location }>(`/v4/locations/${locationId}`, data);
  return response.location;
}

/**
 * Delete a location
 */
export async function deleteLocation(locationId: string): Promise<void> {
  await api.delete(`/v4/locations/${locationId}`);
}

// =============================================================================
// Accounts API Functions (for filter dropdown)
// =============================================================================

interface V4AccountsResponse {
  accounts: V4AccountOption[];
  pageNumber?: number;
  pageLength?: number;
  total?: number;
}

interface V4AccountOption {
  id: string;
  name: string;
  accountNumber?: string | null;
  mongoOrganisationId?: string | null;
}

function normalizeAccount(account: V4AccountOption): Account {
  return {
    id: account.id,
    name: account.name,
    accountNumber: account.accountNumber ?? undefined,
    mongoOrganisationId: account.mongoOrganisationId ?? undefined,
  };
}

/**
 * Get all accounts for the dropdown filter
 */
export async function getAccounts(): Promise<Account[]> {
  const response = await api.get<V4AccountsResponse>('/v4/accounts', {
    pageLength: 100,
    sortKey: 'name',
    sortDirection: 1,
    status: 'active',
  });

  return (response.accounts || []).map(normalizeAccount);
}

/**
 * Search accounts with pagination for async dropdowns.
 */
export async function searchAccountsPaginated({
  search,
  pageNumber,
  pageLength,
}: {
  search: string;
  pageNumber: number;
  pageLength: number;
}): Promise<LocationAccountsResponse> {
  const response = await api.get<V4AccountsResponse>('/v4/accounts', {
    search,
    pageNumber,
    pageLength,
    sortKey: 'name',
    sortDirection: 1,
    status: 'active',
  });

  return {
    accounts: (response.accounts || []).map(normalizeAccount),
    pageNumber: response.pageNumber ?? pageNumber,
    pageLength: response.pageLength ?? pageLength,
    total: response.total ?? response.accounts?.length ?? 0,
  };
}

/**
 * Look up a single account by PostgreSQL account id.
 */
export async function getAccountById(accountId: string): Promise<Account | null> {
  const response = await api.get<{ account?: V4AccountOption | null }>(
    `/v4/accounts/${accountId}`
  );

  return response.account ? normalizeAccount(response.account) : null;
}

// =============================================================================
// Form Options API Functions (for location create/edit modal)
// =============================================================================

/**
 * Get form options (usergroups, transmitters, sensors) for location form
 */
export async function getLocationFormOptions(
  organisationId: string,
  locationId?: string
): Promise<LocationFormOptions> {
  const params: Record<string, string> = {
    organisationId,
  };
  if (locationId) {
    params.locationId = locationId;
  }
  return api.get<LocationFormOptions>('/v4/locations/form-options', params);
}

// =============================================================================
// Changelog API Functions
// =============================================================================

/**
 * Get changelog history for a location
 */
export async function getLocationChangelog(locationId: string): Promise<LocationChangelogResponse> {
  return api.get<LocationChangelogResponse>(`/v4/locations/${locationId}/changelog`);
}

// =============================================================================
// Image API Functions
// =============================================================================

/**
 * Upload or update a location's image
 * First uploads the file to the files endpoint, then updates the location
 */
export async function uploadLocationImage(
  locationId: string,
  file: File
): Promise<LocationImageUploadResponse> {
  // First upload the file to get an imageId
  const formData = new FormData();
  formData.append('file', file);

  // Upload file and get the file ID
  // The V2 files endpoint is used for file storage
  const uploadResponse = await api.upload<{ _id: string }>('/v4/legacy/v2/files', formData);
  const imageId = uploadResponse._id;

  // Then update the location with the new image ID
  return api.post<LocationImageUploadResponse>(`/v4/locations/${locationId}/image`, { imageId });
}

/**
 * Delete a location's image
 */
export async function deleteLocationImage(locationId: string): Promise<LocationImageDeleteResponse> {
  return api.delete<LocationImageDeleteResponse>(`/v4/locations/${locationId}/image`);
}

/**
 * Get the URL for a location's image
 * Returns null if no image is set
 */
export function getLocationImageUrl(
  organisationId: string | undefined,
  imageId: string | undefined
): string | null {
  if (!organisationId || !imageId) return null;
  return `/api/v4/legacy/v2/files/${organisationId}/${imageId}`;
}
